import React, { useMemo } from "react";
import { Container, Row, Col, Button, Card } from "react-bootstrap";
import PageTitle from "Components/Common/PageTitle";
import { StepIndicator } from "./components/StepIndicator";
import { SourceSelection } from "./sections/SourceSelection";
import { CategorySelection } from "./sections/CategorySelection";
import { ReviewMatching } from "./sections/ReviewMatching";
import { SuccessPage } from "./sections/SuccessPage";
import { TimesIcon } from "Components/Icons/TimesIcon";
import { CompetencyMatchingProvider, useCompetencyMatchingContext } from "@/contexts/competencyMatchingContext";

const TOTAL_STEPS = 3;

const CompetencyMatchingContent = () => {
    const { lastSaved, clearAllSavedData, currentStep, isCompleted } = useCompetencyMatchingContext();

    const formatLastSaved = (timestamp: string | null) => {
        if (!timestamp) return "Not saved yet";
        const date = new Date(timestamp);
        return `Saved: ${date.toLocaleDateString()}, ${date.toLocaleTimeString()}`;
    };

    if (isCompleted) {
        return <SuccessPage />;
    }

    const stepContent = useMemo(() => {
        switch (currentStep) {
            case 1:
                return <SourceSelection />;
            case 2:
                return <CategorySelection />;
            case 3:
                return <ReviewMatching />;
            default:
                return <SourceSelection />;
        }
    }, [currentStep]);

    return (
        <Container fluid className="competency-matching-container">
            <Row>
                <Col>
                    <PageTitle pageTitle="Competency Matching" />
                    <Card className="competency-matching-card">
                        <Card.Header className="d-flex justify-content-between">
                            <div className="card-header-content col-6">
                                <div className="header-title-container">
                                    <h2>Competency Matching</h2>
                                </div>
                                <p>
                                    Map your competency from the Examsoft/Canvas
                                    of any other data set that has competencies
                                    competencies/categories to the Enflux
                                    standard categories.
                                </p>
                            </div>
                            <div>
                                <Button
                                    variant="outline-secondary"
                                    className="close-button"
                                    onClick={() => window.history.back()}
                                >
                                    <TimesIcon fill="#333" />
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <div className="step-header-container">
                                <StepIndicator currentStep={currentStep} />
                                <div className="save-status-container">
                                    <Button
                                        variant="outline-primary"
                                        className="complete-matching-btn"
                                        onClick={clearAllSavedData}
                                        disabled={currentStep < TOTAL_STEPS}
                                    >
                                        Complete Matching
                                    </Button>
                                    <div className="saved-timestamp">
                                        {formatLastSaved(lastSaved)}
                                    </div>
                                </div>
                            </div>
                            <div className="step-content">
                                {stepContent}
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

const CompetencyMatchingPage = () => (
    <CompetencyMatchingProvider>
        <CompetencyMatchingContent />
    </CompetencyMatchingProvider>
);

export default CompetencyMatchingPage;
